import type { FileElement } from './file-types';
import type { UserUpdate } from './message-types';

export type Schedule = { startAt: number; endAt?: number };

// renamed to OrgTask to avoid conflict with Task
export interface OrgTask {
  _id: string;
  orgId: string;
  spaceId: string; // might === userId for private tasks
  number: number;
  tagIds: string[];
  assignedTo?: string;
  weight: number; // Integer - position in the backlog/current list. All tasks should have a weight as a child task could easily become orphan if the child has a different assignedTo, or is put into a different stage
  parentId?: string;
  parentWeight?: number; // Integer - position in the parent's sub task list

  timeEstimate?: number; // In milliseconds
  schedule?: Schedule;
  dueAt?: number;
  currentAt?: number;
  completedAt?: number;
  title: string;
  description: string;
  files: FileElement[];
  templateId?: string;

  // Message fields
  userIds: string[];
  userUpdates: UserUpdate[];
  messageNumber: number; // first message starts at 1, if zero messages (like in task) = 0

  createdBy: string;
  localAt: number;
  createdAt: number; // this is only set by the server, and is used in #isNewTask when createdAt === 0
  updatedAt: number; // this is only set by the server
  trashedAt?: number;
  deletedAt?: number;
}

export type OrgTaskPreSave = Omit<OrgTask, 'number'>;

export type OrgTaskFrontEnd = Omit<OrgTask, 'number'> & { number?: number };

export interface OrgTaskCreate {
  spaceId: string;
  salt: string;
  tagIds: string[];
  assignedTo?: string;
  weight: number;
  parentId?: string;
  parentWeight?: number;
  timeEstimate?: number;
  schedule?: Schedule;
  dueAt?: number;
  currentAt?: number;
  completedAt?: number;
  trashedAt?: number;
  title: string;
  description: string;
  files: FileElement[];
  templateId?: string;
  createdBy: string;
  localAt: number;
}

export interface OrgTaskUpdates {
  assignedTo?: string | null;
  weight?: number;
  parentId?: string | null;
  parentWeight?: number;
  timeEstimate?: number | null;
  schedule?: Schedule | null;
  dueAt?: number | null;
  currentAt?: number | null;
  completedAt?: number | null;
  trashedAt?: number | null;
  title?: string;
  description?: string;
  // Commands
  push?: {
    tagIds?: string[];
    files?: FileElement[];
    userIds?: string[];
  };
  pull?: {
    tagIds?: string[];
    fileIds?: string[];
  };
}

export type TaskSetHistory = {
  title?: OrgTask['title'];
  description?: OrgTask['description'];
  assignedTo?: OrgTask['assignedTo'];
  weight?: OrgTask['weight'];
  parentId?: OrgTask['parentId'];
  parentWeight?: OrgTask['parentWeight'];
  timeEstimate?: OrgTask['timeEstimate'];
  schedule?: OrgTask['schedule'];
  dueAt?: OrgTask['dueAt'];
  currentAt?: OrgTask['currentAt'];
  completedAt?: OrgTask['completedAt'];
  trashedAt?: OrgTask['trashedAt'];
};

export type TaskUnsetHistory =
  | 'assignedTo'
  | 'parentId'
  | 'timeEstimate'
  | 'schedule'
  | 'dueAt'
  | 'currentAt'
  | 'completedAt'
  | 'trashedAt';

export interface TaskHistory {
  _id: string;
  orgId: string;
  spaceId: string;
  taskId: string;
  updatedBy: string;
  updatedAt: number;

  set?: TaskSetHistory;
  unset?: TaskUnsetHistory[];
  push?: {
    tagIds?: string[];
    files?: FileElement[];
  };
  pull?: {
    tagIds?: string[];
    fileIds?: string[];
  };
}

// 9007199254740991 - MAX_SAFE_INTEGER
// taskMaxWeight is less than half here because we want to safely be able to find the average
export const taskMaxWeight = 4000000000000000;
export const incrementalTaskWeight = 10000000000;

export const taskTitleMaxLength = 256;
export const taskDescriptionMaxLength = 50000;

export function placeMidWeight(
  weight1: number | undefined,
  weight2: number | undefined
): number {
  if (
    (typeof weight1 === 'number' &&
      (weight1 < 0 || taskMaxWeight <= weight1)) ||
    (typeof weight2 === 'number' && (weight2 <= 0 || taskMaxWeight < weight2))
  ) {
    throw new Error('Weight not between range: 0-' + taskMaxWeight);
  }

  // Add some randomness to avoid two tasks with the same weight when two
  // people try to add tasks at the same time at the end.

  const randomOffset = Math.floor(
    incrementalTaskWeight * 0.1 * (Math.random() - 0.5)
  );

  if (typeof weight1 === 'undefined' && typeof weight2 === 'undefined') {
    return safeDivideBy2(taskMaxWeight) + randomOffset;
  }

  // Set some defaults to prevent overflowing the ends
  if (
    typeof weight1 === 'undefined' &&
    typeof weight2 === 'number' &&
    weight2 < incrementalTaskWeight * 2
  ) {
    weight1 = 0;
  }
  if (
    typeof weight1 === 'number' &&
    typeof weight2 === 'undefined' &&
    weight1 > taskMaxWeight - incrementalTaskWeight * 2
  ) {
    weight2 = taskMaxWeight;
  }

  // If we have both ends divide by to and add some randomness
  if (typeof weight1 === 'number' && typeof weight2 === 'number') {
    return (
      safeDivideBy2(weight1 + weight2) +
      Math.floor((weight2 - weight1) * 0.1 * (Math.random() - 0.5))
    );
  }

  if (typeof weight1 === 'number' && typeof weight2 === 'undefined') {
    return weight1 + incrementalTaskWeight + randomOffset;
  }

  if (typeof weight2 === 'number' && typeof weight1 === 'undefined') {
    return weight2 - incrementalTaskWeight + randomOffset;
  }
  throw new Error('unexpected state');
}

function safeDivideBy2(value: number) {
  if (value % 2 === 1) value--;
  return value / 2;
}
